<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-ranking-star me-2" style="color: #a855f7;"></i>{{ $t('Staff Performance') }}
          </h5>
          <small class="text-muted">{{ $t('Staff') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Staff Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-users me-2 text-primary"></i>{{ $t('Staff Performance') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">#</th>
                <th>{{ $t('Staff Name') }}</th>
                <th class="text-end">{{ $t('Orders Taken') }}</th>
                <th class="text-end">{{ $t('Total Sales') }}</th>
                <th class="text-end">{{ $t('Avg. per Order') }}</th>
                <th class="text-end">{{ $t('Voided Bills') }}</th>
                <th class="text-end">{{ $t('Items Cancelled') }}</th>
                <th class="text-end pe-3">{{ $t('After Kitchen Started') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!staffData.length && !loading">
                <td colspan="8" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(s, i) in staffData" :key="s.staff_id || i">
                <td class="ps-3">{{ i + 1 }}</td>
                <td>
                  <i class="fa-solid fa-circle-user me-2 text-muted"></i>
                  <span class="fw-medium">{{ s.name }}</span>
                </td>
                <td class="text-end">{{ s.orders }}</td>
                <td class="text-end fw-semibold">{{ formatCurrency(s.sales) }}</td>
                <td class="text-end">{{ formatCurrency(s.avg) }}</td>
                <td class="text-end">
                  <span :class="s.cancellations > 3 ? 'text-danger' : 'text-muted'">{{ s.cancellations }}</span>
                </td>
                <td class="text-end">{{ s.item_cancellations || 0 }}</td>
                <!-- ยกเลิกตอนครัวเริ่มทำแล้ว = วัตถุดิบหมดไปแล้ว ตัวที่เจ้าของร้านควรดูก่อน -->
                <td class="text-end pe-3">
                  <span :class="s.items_after_kitchen > 0 ? 'text-danger fw-semibold' : 'text-muted'">{{ s.items_after_kitchen || 0 }}</span>
                  <div v-if="s.item_cancelled_amount" class="small text-muted">{{ formatCurrency(s.item_cancelled_amount) }}</div>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="staffData.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="2">{{ $t('Total') }}</td>
                <td class="text-end">{{ staffTotals.orders }}</td>
                <td class="text-end">{{ formatCurrency(staffTotals.sales) }}</td>
                <td class="text-end">{{ formatCurrency(staffTotals.avg) }}</td>
                <td class="text-end">{{ staffTotals.cancellations }}</td>
                <td class="text-end">{{ staffTotals.item_cancellations }}</td>
                <td class="text-end pe-3">
                  {{ staffTotals.items_after_kitchen }}
                  <div class="small text-muted">{{ formatCurrency(staffTotals.item_cancelled_amount) }}</div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportStaffPerformance',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Staff Performance',
      staff: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.staffPerformance(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.staff    = payload.staff || [];
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Staff Performance'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: this.$t('Staff Name'), style: 'tableHeader' },
                { text: this.$t('Orders Taken'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total Sales'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Avg. per Order'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Voided Bills'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Items Cancelled'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('After Kitchen Started'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.staffData.map((s, i) => [
                { text: String(i + 1), style: 'tableCell' },
                { text: s.name, style: 'tableCell' },
                { text: String(s.orders), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(s.sales), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(s.avg), style: 'tableCell', alignment: 'right' },
                { text: String(s.cancellations), style: 'tableCell', alignment: 'right', color: s.cancellations > 3 ? '#ef4444' : undefined },
                { text: String(s.item_cancellations || 0), style: 'tableCell', alignment: 'right' },
                { text: `${s.items_after_kitchen || 0} (${this.formatCurrencyRaw(s.item_cancelled_amount || 0)})`, style: 'tableCell', alignment: 'right', color: s.items_after_kitchen > 0 ? '#ef4444' : undefined },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 2 }, {},
                { text: String(this.staffTotals.orders), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.staffTotals.sales), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.staffTotals.avg), style: 'tableHeader', alignment: 'right' },
                { text: String(this.staffTotals.cancellations), style: 'tableHeader', alignment: 'right' },
                { text: String(this.staffTotals.item_cancellations), style: 'tableHeader', alignment: 'right' },
                { text: `${this.staffTotals.items_after_kitchen} (${this.formatCurrencyRaw(this.staffTotals.item_cancelled_amount)})`, style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },
  },
  computed: {
    staffData() {
      return this.staff || [];
    },
    staffTotals() {
      const t = this.staffData.reduce((acc, s) => ({
        orders: acc.orders + (s.orders || 0),
        sales:  acc.sales  + (s.sales  || 0),
        cancellations: acc.cancellations + (s.cancellations || 0),
        item_cancellations: acc.item_cancellations + (s.item_cancellations || 0),
        items_after_kitchen: acc.items_after_kitchen + (s.items_after_kitchen || 0),
        item_cancelled_amount: acc.item_cancelled_amount + (s.item_cancelled_amount || 0),
      }), { orders: 0, sales: 0, cancellations: 0, item_cancellations: 0, items_after_kitchen: 0, item_cancelled_amount: 0 });
      t.avg = t.orders > 0 ? t.sales / t.orders : 0;
      return t;
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
